import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {   getAllUsers,   updateUserRole,   adjustWalletBalance,   getPendingWithdrawals,   updateWithdrawalStatus,  overrideMatchResult,  toggleMatchVisibility,
  getDailyUserStats,
  getActiveUsers,
  getAllWithdrawals,
  updateWithdrawalPaymentStatus,
  getUserReferralInfo,
  getDailyLoginStats,
  getLoginRewardsStats,
  searchUsers
} from '../services/adminService';
import AdminMatches from '../pages/AdminMatches';
import AdminSupportMessages from './AdminSupportMessages';
import AdminDailyCode from './AdminDailyCode';

const AdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [filteredWithdrawals, setFilteredWithdrawals] = useState([]);
  const [withdrawalStatusFilter, setWithdrawalStatusFilter] = useState('all');
  const [matches, setMatches] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // New states for referral modal
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [selectedUserReferrals, setSelectedUserReferrals] = useState(null);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  
  // States for login stats (moved from renderStatsTab)
  const [loginStats, setLoginStats] = useState([]);
  const [rewardStats, setRewardStats] = useState({
    totalRewards: 0,
    usersWithRewards: 0,
    averageRewards: 0,
    biggestRewardEarner: { amount: 0 }
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Load data based on active tab
  useEffect(() => {
    fetchData();
    // If withdrawal tab is active, fetch withdrawals immediately
    const path = location.pathname;
    if (path.includes('/admin/withdrawals')) {
      fetchWithdrawals();
    }
  }, [location.pathname]);
  
  // Apply withdrawal filters when withdrawals or filter changes
  useEffect(() => {
    if (withdrawalStatusFilter === 'all') {
      setFilteredWithdrawals(withdrawals);
    } else {
      setFilteredWithdrawals(withdrawals.filter(w => {
        if (withdrawalStatusFilter === 'paid' || withdrawalStatusFilter === 'unpaid') {
          return w.paymentStatus === withdrawalStatusFilter;
        }
        return w.status === withdrawalStatusFilter;
      }));
    }
  }, [withdrawals, withdrawalStatusFilter]);
  
  // Fetch login stats when stats tab is active (moved from renderStatsTab)
  useEffect(() => {
    const fetchLoginStats = async () => {
      try {
        const loginData = await getDailyLoginStats(30);
        setLoginStats(loginData);
        
        const rewardsData = await getLoginRewardsStats();
        setRewardStats(rewardsData);
      } catch (err) {
        console.error("Error fetching login stats:", err);
      }
    };
    
    if (activeTab === 'stats') {
      fetchLoginStats();
    }
  }, [activeTab]);
  
  // Function to fetch data based on active tab
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Determine active tab from URL
      const path = location.pathname;
      let tab = 'users';
      
      if (path.includes('/admin/matches')) tab = 'matches';
      else if (path.includes('/admin/withdrawals')) tab = 'withdrawals';
      else if (path.includes('/admin/stats')) tab = 'stats';
      else if (path.includes('/admin/support')) tab = 'support';
      else if (path.includes('/admin/users')) tab = 'users';
      else if (path.includes('/admin/dailycode')) tab = 'dailycode';
      
      setActiveTab(tab);
      
      // Fetch data based on tab
      switch (tab) {
        case 'users':
          const usersData = await getAllUsers();
          setUsers(usersData);
          break;
        case 'withdrawals':
          await fetchWithdrawals();
          break;
        case 'matches':
          // This would come from a match service
          // For now, we'll use dummy data
          setMatches([
            { id: 1, teams: 'IND vs AUS', date: '2023-06-11', status: 'Live', isVisible: true },
            { id: 2, teams: 'ENG vs NZ', date: '2023-06-15', status: 'Upcoming', isVisible: true }
          ]);
          break;
        case 'stats':
          const statsData = await getDailyUserStats(30);
          setDailyStats(statsData);
          const activeUsersData = await getActiveUsers(7);
          setActiveUsers(activeUsersData);
          break;
        case 'support':
          // Support messages are fetched within the AdminSupportMessages component
          break;
        case 'dailycode':
          // Daily code is managed within the AdminDailyCode component
          break;
        default:
          break;
      }
    } catch (err) {
      console.error("Error fetching admin data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (tab) => {
    navigate(`/admin/${tab === 'users' ? '' : tab}`);
  };
  
  // Handle making a user admin
  const handleMakeAdmin = async (uid) => {
    try {
      await updateUserRole(uid, 'admin');
      setUsers(users.map(user => 
        user.uid === uid ? { ...user, role: 'admin' } : user
      ));
    } catch (err) {
      console.error("Error updating user role:", err);
      setError("Failed to update user role.");
    }
  };
  
  // Handle removing admin role
  const handleRemoveAdmin = async (uid) => {
    try {
      await updateUserRole(uid, 'user');
      setUsers(users.map(user => 
        user.uid === uid ? { ...user, role: 'user' } : user
      ));
    } catch (err) {
      console.error("Error updating user role:", err);
      setError("Failed to update user role.");
    }
  };
  
  // Handle wallet balance adjustment
  const handleAdjustWallet = async (uid, wallet, newBalance) => {
  try {
    const numericBalance = parseInt(newBalance);
    if (isNaN(numericBalance)) {
      setError("Please enter a valid number for wallet balance");
      return;
    }
    
    // Call the service and receive the updated balance
    const updatedBalance = await adjustWalletBalance(uid, wallet, numericBalance);
    
    // Update the UI with the exact balance returned from the database
    setUsers(users.map(user => 
      user.uid === uid ? { 
        ...user, 
        [wallet]: updatedBalance  // direct field update
      } : user
    ));
  } catch (err) {
    console.error("Error adjusting wallet balance:", err);
    setError("Failed to adjust wallet balance. Please try again.");
  }
};

  
  // Function to fetch withdrawal data
  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("=== Fetching withdrawal data... ===");
      
      // Get fresh data
      const withdrawalsData = await getAllWithdrawals();
      
      console.log("Fetched withdrawal data:", 
        withdrawalsData, 
        "Length:", withdrawalsData.length, 
        "Sample:", withdrawalsData.length > 0 ? withdrawalsData[0] : "No data"
      );
      
      if (withdrawalsData.length === 0) {
        console.warn("No withdrawal data returned from getAllWithdrawals");
      }
      
      // Update state
      setWithdrawals(withdrawalsData);
      
      // Make sure to update filtered withdrawals too
      if (withdrawalStatusFilter === 'all') {
        console.log("Setting all withdrawals to filtered view:", withdrawalsData.length);
        setFilteredWithdrawals(withdrawalsData);
      } else {
        const filtered = withdrawalsData.filter(w => {
          if (withdrawalStatusFilter === 'paid' || withdrawalStatusFilter === 'unpaid') {
            return w.paymentStatus === withdrawalStatusFilter;
          }
          return w.status === withdrawalStatusFilter;
        });
        console.log(`Setting filtered withdrawals (${withdrawalStatusFilter}):`, filtered.length);
        setFilteredWithdrawals(filtered);
      }
      
      console.log("=== Fetch withdrawals complete ===");
    } catch (err) {
      console.error("Error fetching withdrawals:", err);
      setError(`Failed to fetch withdrawal data: ${err.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };
  
      
  // Handle withdrawal approval
  const handleApproveWithdrawal = async (id) => {
    try {
      await updateWithdrawalStatus(id, 'approved');
      // Refetch all withdrawals to ensure data is fresh
      const withdrawalsData = await getAllWithdrawals();
      setWithdrawals(withdrawalsData);
    } catch (err) {
      console.error("Error approving withdrawal:", err);
      setError("Failed to approve withdrawal.");
    }
  };
  
  // Handle withdrawal rejection
  const handleRejectWithdrawal = async (id) => {
    try {
      await updateWithdrawalStatus(id, 'rejected');
      // Refetch all withdrawals to ensure data is fresh
      const withdrawalsData = await getAllWithdrawals();
      setWithdrawals(withdrawalsData);
    } catch (err) {
      console.error("Error rejecting withdrawal:", err);
      setError("Failed to reject withdrawal.");
    }
  };
  
  // Handle marking withdrawal as paid
  const handleMarkAsPaid = async (id) => {
    try {
      await updateWithdrawalPaymentStatus(id, 'paid');
      // Refetch all withdrawals to ensure data is fresh
      const withdrawalsData = await getAllWithdrawals();
      setWithdrawals(withdrawalsData);
    } catch (err) {
      console.error("Error marking withdrawal as paid:", err);
      setError("Failed to mark withdrawal as paid.");
    }
  };
  
  // Handle marking withdrawal as unpaid
  const handleMarkAsUnpaid = async (id) => {
    try {
      await updateWithdrawalPaymentStatus(id, 'unpaid');
      // Refetch all withdrawals to ensure data is fresh
      const withdrawalsData = await getAllWithdrawals();
      setWithdrawals(withdrawalsData);
    } catch (err) {
      console.error("Error marking withdrawal as unpaid:", err);
      setError("Failed to mark withdrawal as unpaid.");
    }
  };
  
  // Handle match visibility toggle
  const handleToggleMatchVisibility = async (id, isVisible) => {
    try {
      await toggleMatchVisibility(id, !isVisible);
      setMatches(matches.map(match => 
        match.id === id ? { ...match, isVisible: !isVisible } : match
      ));
    } catch (err) {
      console.error("Error toggling match visibility:", err);
      setError("Failed to toggle match visibility.");
    }
  };
  
  // Handle withdrawal status filter change
  const handleWithdrawalFilterChange = (e) => {
    setWithdrawalStatusFilter(e.target.value);
  };
  
  // Helper function to safely get UPI ID from withdrawal data
  const getUpiId = (withdrawal) => {
    if (withdrawal.accountDetails && withdrawal.accountDetails.upiId) {
      return withdrawal.accountDetails.upiId;
    }
    
    // Sometimes it might be stored as a string directly
    if (typeof withdrawal.accountDetails === 'string') {
      return withdrawal.accountDetails;
    }
    
    // It might be stored in a different field
    if (withdrawal.upiId) {
      return withdrawal.upiId;
    }
    
    return 'N/A';
  };
  
  // New function to handle viewing referral information
  const handleViewReferrals = async (uid) => {
    try {
      setLoadingReferrals(true);
      setError(null);
      
      const referralInfo = await getUserReferralInfo(uid);
      
      if (referralInfo.error) {
        setError(referralInfo.error);
        return;
      }
      
      setSelectedUserReferrals(referralInfo);
      setShowReferralModal(true);
    } catch (err) {
      console.error("Error fetching referral info:", err);
      setError("Failed to fetch referral information.");
    } finally {
      setLoadingReferrals(false);
    }
  };
  
  // Handle search users
  const handleSearchUsers = async (e) => {
    e.preventDefault();
    setIsSearching(true);
    
    try {
      const results = await searchUsers(searchTerm);
      setUsers(results);
    } catch (err) {
      console.error("Error searching users:", err);
      setError("Failed to search users. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };
  
  // Render horizontal navigation
  const renderNavTabs = () => (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <ul className="flex flex-wrap -mb-px md:flex-nowrap overflow-x-auto scrollbar-hide space-x-1">
        <li className="mr-0">
          <button 
            onClick={() => handleTabChange('users')} 
            className={`inline-flex items-center py-2 px-2 md:px-4 text-xs md:text-sm font-medium rounded-t-lg ${
              activeTab === 'users' 
                ? 'text-blue-600 border-b-2 border-blue-600 active dark:text-blue-500 dark:border-blue-500' 
                : 'text-gray-500 hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
            </svg>
            <span className="md:inline">Users</span>
          </button>
        </li>
        <li className="mr-0">
          <button 
            onClick={() => handleTabChange('withdrawals')} 
            className={`inline-flex items-center py-2 px-2 md:px-4 text-xs md:text-sm font-medium rounded-t-lg ${
              activeTab === 'withdrawals' 
                ? 'text-blue-600 border-b-2 border-blue-600 active dark:text-blue-500 dark:border-blue-500' 
                : 'text-gray-500 hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"></path>
              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"></path>
            </svg>
            <span className="md:inline">Withdrawals</span>
          </button>
        </li>
        <li className="mr-0">
          <button 
            onClick={() => handleTabChange('matches')} 
            className={`inline-flex items-center py-2 px-2 md:px-4 text-xs md:text-sm font-medium rounded-t-lg ${
              activeTab === 'matches' 
                ? 'text-blue-600 border-b-2 border-blue-600 active dark:text-blue-500 dark:border-blue-500' 
                : 'text-gray-500 hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
            </svg>
            <span className="md:inline">Matches</span>
          </button>
        </li>
        <li className="mr-0">
          <button 
            onClick={() => handleTabChange('stats')} 
            className={`inline-flex items-center py-2 px-2 md:px-4 text-xs md:text-sm font-medium rounded-t-lg ${
              activeTab === 'stats' 
                ? 'text-blue-600 border-b-2 border-blue-600 active dark:text-blue-500 dark:border-blue-500' 
                : 'text-gray-500 hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
              <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
            </svg>
            <span className="md:inline">Stats</span>
          </button>
        </li>
        <li className="mr-0">
          <button 
            onClick={() => handleTabChange('support')} 
            className={`inline-flex items-center py-2 px-2 md:px-4 text-xs md:text-sm font-medium rounded-t-lg ${
              activeTab === 'support' 
                ? 'text-blue-600 border-b-2 border-blue-600 active dark:text-blue-500 dark:border-blue-500' 
                : 'text-gray-500 hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z" clipRule="evenodd"></path>
            </svg>
            <span className="md:inline">Support</span>
          </button>
        </li>
        <li className="mr-0">
          <button 
            onClick={() => handleTabChange('dailycode')} 
            className={`inline-flex items-center py-2 px-2 md:px-4 text-xs md:text-sm font-medium rounded-t-lg ${
              activeTab === 'dailycode' 
                ? 'text-blue-600 border-b-2 border-blue-600 active dark:text-blue-500 dark:border-blue-500' 
                : 'text-gray-500 hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
            </svg>
            <span className="md:inline">Daily Code</span>
          </button>
        </li>
        <li className="mr-0">
          <Link
            to="/admin/blog-rewards"
            className="inline-flex items-center py-2 px-2 md:px-4 text-xs md:text-sm font-medium rounded-t-lg text-gray-500 hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 mr-1 md:mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z"></path>
            </svg>
            <span className="md:inline">Blog Rewards</span>
          </Link>
        </li>
      </ul>
    </div>
  );
  
  // Render users tab
  const renderUsersTab = () => (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-3 sm:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-0">Manage Users</h2>
        <div className="flex items-center w-full md:w-auto">
          <form onSubmit={handleSearchUsers} className="relative w-full md:w-auto">
            <input
              type="text"
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white w-full"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <button 
              type="submit" 
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              disabled={isSearching}
            >
              {isSearching ? (
                <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="h-5 w-5 text-gray-400 hover:text-gray-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          </form>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 sm:mb-6 rounded-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p>{error}</p>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-lg text-gray-500 dark:text-gray-400">Loading users...</span>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <div className="bg-gray-50 dark:bg-gray-700 hidden md:block">
              <div className="grid grid-cols-12 px-6 py-3">
                <div className="col-span-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</div>
                <div className="col-span-1 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Role</div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Wallet A</div>
                <div className="col-span-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Wallet B</div>
                <div className="col-span-1 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Referrals</div>
                <div className="col-span-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {users.map(user => (
                <div key={user.uid} className="divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-700">
                  {/* Mobile view */}
                  <div className="md:hidden p-4">
                    <div className="flex items-center mb-3">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300">
                        {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{user.displayName || 'No Name'}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.email} {user.username && `(@${user.username})`}
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-y-2 text-sm mb-3">
                      <div className="text-gray-500 dark:text-gray-400">Role:</div>
                      <div className="text-gray-900 dark:text-white">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.role === 'admin' 
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {user.role || 'user'}
                        </span>
                      </div>
                      
                      <div className="text-gray-500 dark:text-gray-400">Wallet A:</div>
                      <div className="text-gray-900 dark:text-white">
                        <input 
                          type="text" 
                          className="border border-gray-300 rounded-md p-1 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          defaultValue={user.walletA || 0}
                          onBlur={(e) => handleAdjustWallet(user.uid, 'walletA', e.target.value)}
                        />
                      </div>
                      
                      <div className="text-gray-500 dark:text-gray-400">Wallet B:</div>
                      <div className="text-gray-900 dark:text-white">
                        <input 
                          type="text" 
                          className="border border-gray-300 rounded-md p-1 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          defaultValue={user.walletB || 0}
                          onBlur={(e) => handleAdjustWallet(user.uid, 'walletB', e.target.value)}
                        />
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-3">
                      {user.role === 'admin' ? (
                        <button
                          onClick={() => handleRemoveAdmin(user.uid)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800"
                        >
                          Remove Admin
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMakeAdmin(user.uid)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                        >
                          Make Admin
                        </button>
                      )}
                      <button
                        onClick={() => handleViewReferrals(user.uid)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-green-700 bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800"
                      >
                        View Referrals
                      </button>
                    </div>
                  </div>
                  
                  {/* Desktop view */}
                  <div className="hidden md:grid md:grid-cols-12 md:px-6 md:py-4 hover:bg-gray-50 dark:hover:bg-gray-750">
                    <div className="col-span-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300">
                          {user.displayName ? user.displayName.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{user.displayName || 'No Name'}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {user.email} {user.username && `(@${user.username})`}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-span-1 flex items-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        user.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' 
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {user.role || 'user'}
                      </span>
                    </div>
                    
                    <div className="col-span-2 flex items-center">
                      <input 
                        type="text" 
                        className="border border-gray-300 rounded-md p-1 w-24 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        defaultValue={user.walletA || 0}
                        onBlur={(e) => handleAdjustWallet(user.uid, 'walletA', e.target.value)}
                      />
                    </div>
                    
                    <div className="col-span-2 flex items-center">
                      <input 
                        type="text" 
                        className="border border-gray-300 rounded-md p-1 w-24 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        defaultValue={user.walletB || 0}
                        onBlur={(e) => handleAdjustWallet(user.uid, 'walletB', e.target.value)}
                      />
                    </div>
                    
                    <div className="col-span-1 flex items-center">
                      <button
                        onClick={() => handleViewReferrals(user.uid)}
                        className="text-sm text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        View
                      </button>
                    </div>
                    
                    <div className="col-span-3 flex items-center space-x-2">
                      {user.role === 'admin' ? (
                        <button
                          onClick={() => handleRemoveAdmin(user.uid)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800"
                        >
                          Remove Admin
                        </button>
                      ) : (
                        <button
                          onClick={() => handleMakeAdmin(user.uid)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                        >
                          Make Admin
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {users.length === 0 && !loading && (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">No users found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  // Render withdrawals tab
  const renderWithdrawalsTab = () => (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-0">
          <span className="flex items-center">
            <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Withdrawal Management
          </span>
        </h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <select
              className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg pl-4 pr-10 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto"
              value={withdrawalStatusFilter}
              onChange={handleWithdrawalFilterChange}
            >
              <option value="all">All Withdrawals</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </div>
          <button
            onClick={fetchWithdrawals}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            Refresh
          </button>
        </div>
      </div>
      
      {/* Dashboard Cards */}
      {!loading && filteredWithdrawals.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-lg shadow-sm border border-blue-200 dark:border-blue-800">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-500/10 text-blue-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Requests</h3>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {withdrawals.length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 p-4 rounded-lg shadow-sm border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-500/10 text-yellow-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending</h3>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {withdrawals.filter(w => w.status === 'pending').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-lg shadow-sm border border-green-200 dark:border-green-800">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-500/10 text-green-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Approved</h3>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {withdrawals.filter(w => w.status === 'approved').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-lg shadow-sm border border-purple-200 dark:border-purple-800">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-500/10 text-purple-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Amount</h3>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  ₹{withdrawals.reduce((sum, w) => sum + (parseInt(w.amount) || 0), 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 p-4 mb-6 rounded-lg animate-fadeIn">
          <div className="flex items-center">
            <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p>{error}</p>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <svg className="animate-spin h-12 w-12 text-blue-500 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-lg text-gray-500 dark:text-gray-400">Loading withdrawals...</span>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
          {filteredWithdrawals.length > 0 ? (
            <>
              {/* Desktop view */}
              <div className="hidden md:block">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Payment Info</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Request Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredWithdrawals.map(withdrawal => (
                      <tr key={withdrawal.id} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors duration-150">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                              <span className="text-gray-600 dark:text-gray-300 font-medium">
                                {(withdrawal.username || withdrawal.userEmail || '').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {withdrawal.userEmail || withdrawal.userId}
                              </div>
                              {withdrawal.username && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  @{withdrawal.username}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            ₹{withdrawal.amount}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500 dark:text-gray-400 break-all">
                            {getUpiId(withdrawal)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {new Date(withdrawal.timestamp?.seconds * 1000).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              withdrawal.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' 
                                : withdrawal.status === 'approved' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {withdrawal.status}
                            </span>
                            
                            {withdrawal.status === 'approved' && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                withdrawal.paymentStatus === 'paid' 
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {withdrawal.paymentStatus || 'unpaid'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div className="flex space-x-2">
                            {withdrawal.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleApproveWithdrawal(withdrawal.id)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150"
                                >
                                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                  </svg>
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleRejectWithdrawal(withdrawal.id)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-150"
                                >
                                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                  </svg>
                                  Reject
                                </button>
                              </>
                            )}
                            
                            {withdrawal.status === 'approved' && (
                              <>
                                {withdrawal.paymentStatus !== 'paid' ? (
                                  <button
                                    onClick={() => handleMarkAsPaid(withdrawal.id)}
                                    className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                                  >
                                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    Mark as Paid
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleMarkAsUnpaid(withdrawal.id)}
                                    className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-150"
                                  >
                                    <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                                    </svg>
                                    Mark as Unpaid
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Mobile view */}
              <div className="md:hidden">
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredWithdrawals.map(withdrawal => (
                    <div key={withdrawal.id} className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                            <span className="text-gray-600 dark:text-gray-300 font-medium">
                              {(withdrawal.username || withdrawal.userEmail || '').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {withdrawal.userEmail || withdrawal.userId}
                            </div>
                            {withdrawal.username && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                @{withdrawal.username}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={`px-2 py-1 text-xs rounded-full ${
                          withdrawal.status === 'pending' 
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' 
                            : withdrawal.status === 'approved' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>
                          {withdrawal.status}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-2">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Amount</div>
                          <div className="font-medium text-gray-900 dark:text-white">₹{withdrawal.amount}</div>
                        </div>
                        
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-2">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Date</div>
                          <div className="font-medium text-gray-900 dark:text-white text-xs">
                            {new Date(withdrawal.timestamp?.seconds * 1000).toLocaleDateString()}
                          </div>
                        </div>
                        
                        <div className="col-span-2 bg-gray-50 dark:bg-gray-700 rounded-md p-2">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Payment Info</div>
                          <div className="font-medium text-gray-900 dark:text-white break-all text-xs">
                            {getUpiId(withdrawal)}
                          </div>
                        </div>
                      </div>
                      
                      {withdrawal.status === 'approved' && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500 dark:text-gray-400">Payment Status:</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            withdrawal.paymentStatus === 'paid' 
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {withdrawal.paymentStatus || 'unpaid'}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                        {withdrawal.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApproveWithdrawal(withdrawal.id)}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                              </svg>
                              Approve
                            </button>
                            <button
                              onClick={() => handleRejectWithdrawal(withdrawal.id)}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-150"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                              </svg>
                              Reject
                            </button>
                          </>
                        )}
                        
                        {withdrawal.status === 'approved' && (
                          <>
                            {withdrawal.paymentStatus !== 'paid' ? (
                              <button
                                onClick={() => handleMarkAsPaid(withdrawal.id)}
                                className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
                              >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                Mark as Paid
                              </button>
                            ) : (
                              <button
                                onClick={() => handleMarkAsUnpaid(withdrawal.id)}
                                className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-150"
                              >
                                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                                </svg>
                                Mark as Unpaid
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">No withdrawals found</p>
              <p className="text-gray-400 dark:text-gray-500 mt-1">Try changing your filter or check back later</p>
              <button
                onClick={fetchWithdrawals}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Refresh Data
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  // Render matches tab
  const renderMatchesTab = () => (
    <AdminMatches/>
  );
  
  // Render stats tab  
  const renderStatsTab = () => {
    return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-0">User Statistics</h2>
      </div>
      
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-lg">
          <div className="flex items-center">
            <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p>{error}</p>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-lg text-gray-500 dark:text-gray-400">Loading stats...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Existing stats sections */}
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Daily Active Users</h3>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-300">Last 7 days:</span>
              <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{activeUsers.length}</span>
            </div>
          </div>
          
    
          {/* Login Rewards Statistics */}
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Login Rewards</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Rewards Given</p>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">{rewardStats.totalRewards.toLocaleString()} coins</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Users With Rewards</p>
                <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{rewardStats.usersWithRewards}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Average Per User</p>
                <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{rewardStats.averageRewards.toLocaleString()} coins</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">Top Earner Amount</p>
                <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{rewardStats.biggestRewardEarner.amount.toLocaleString()} coins</p>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400">Top Reward Earner</p>
              <p className="text-md font-medium text-gray-800 dark:text-gray-200 truncate">{rewardStats.biggestRewardEarner.email || 'No data available'}</p>
            </div>
          </div>
          
          
        </div>
      )}
    </div>
  )};

  const renderSupportTab = () => (
    <AdminSupportMessages />
  );

  // Render daily code tab
  const renderDailyCodeTab = () => (
    <div className="mt-6">
      <AdminDailyCode />
    </div>
  );

  // Add referral modal component
  const ReferralModal = () => {
    if (!selectedUserReferrals) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                Referral Information
              </h2>
              <button 
                onClick={() => setShowReferralModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          <div className="p-4 sm:p-6">
            {/* User Info */}
            <div className="mb-4 sm:mb-6">
              <h3 className="text-md sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">User Details</h3>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 rounded-lg">
                <p className="text-sm sm:text-base mb-1"><span className="font-medium">Name:</span> {selectedUserReferrals.user.displayName}</p>
                <p className="text-sm sm:text-base mb-1"><span className="font-medium">Email:</span> {selectedUserReferrals.user.email}</p>
                <p className="text-sm sm:text-base mb-1"><span className="font-medium">Username:</span> {selectedUserReferrals.user.username}</p>
                <p className="text-sm sm:text-base"><span className="font-medium">Referral Code:</span> {selectedUserReferrals.user.referralCode}</p>
              </div>
            </div>
            
            {/* Referrer Info */}
            <div className="mb-4 sm:mb-6">
              <h3 className="text-md sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">Referred By</h3>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 rounded-lg">
                {selectedUserReferrals.referrer ? (
                  <>
                    <p className="text-sm sm:text-base mb-1"><span className="font-medium">Name:</span> {selectedUserReferrals.referrer.displayName}</p>
                    <p className="text-sm sm:text-base mb-1"><span className="font-medium">Email:</span> {selectedUserReferrals.referrer.email}</p>
                    <p className="text-sm sm:text-base mb-1"><span className="font-medium">Username:</span> {selectedUserReferrals.referrer.username}</p>
                    <p className="text-sm sm:text-base mb-1"><span className="font-medium">Referral Code:</span> {selectedUserReferrals.referrer.referralCode}</p>
                    <button 
                      onClick={() => {
                        setShowReferralModal(false);
                        handleViewReferrals(selectedUserReferrals.referrer.uid);
                      }}
                      className="mt-2 px-3 py-1 bg-blue-600 text-white text-xs sm:text-sm rounded hover:bg-blue-700"
                    >
                      View Referrer's Details
                    </button>
                  </>
                ) : (
                  <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">This user was not referred by anyone</p>
                )}
              </div>
            </div>
            
            {/* Referral Stats */}
            <div className="mb-4 sm:mb-6">
              <h3 className="text-md sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">Referral Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 sm:p-4 rounded-lg">
                  <p className="text-xs sm:text-sm text-blue-600 dark:text-blue-400">Total Users Referred</p>
                  <p className="text-xl sm:text-2xl font-bold">{selectedUserReferrals.referred.count}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-3 sm:p-4 rounded-lg">
                  <p className="text-xs sm:text-sm text-green-600 dark:text-green-400">Total Earnings from Referrals</p>
                  <p className="text-xl sm:text-2xl font-bold">
                    {selectedUserReferrals.stats.totalEarnings.toLocaleString()} coins
                  </p>
                  {selectedUserReferrals.stats.totalEarnings !== selectedUserReferrals.stats.expectedEarnings && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Expected: {selectedUserReferrals.stats.expectedEarnings.toLocaleString()} coins
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Users Referred List */}
            <div>
              <h3 className="text-md sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">Users Referred</h3>
              {selectedUserReferrals.referred.users.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    {/* Table header - visible only on desktop */}
                    <div className="bg-gray-50 dark:bg-gray-700 hidden md:block">
                      <div className="grid grid-cols-10 px-4 py-3">
                        <div className="col-span-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</div>
                        <div className="col-span-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</div>
                        <div className="col-span-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Join Date</div>
                        <div className="col-span-1 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</div>
                      </div>
                    </div>
                    
                    {/* User list */}
                    <div className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {selectedUserReferrals.referred.users.map(user => (
                        <div key={user.uid} className="divide-y md:divide-y-0 divide-gray-100 dark:divide-gray-700">
                          {/* Mobile view */}
                          <div className="md:hidden p-3">
                            <div className="flex items-center mb-2">
                              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300">
                                {user.displayName.charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">{user.displayName}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{user.username}</div>
                              </div>
                            </div>
                            
                            <div className="ml-11 mb-2">
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-medium">Email:</span> {user.email}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-medium">Joined:</span> {user.joinDate}
                              </div>
                            </div>
                            
                            <div className="ml-11">
                              <button 
                                onClick={() => {
                                  setShowReferralModal(false);
                                  handleViewReferrals(user.uid);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                View Details
                              </button>
                            </div>
                          </div>
                          
                          {/* Desktop view */}
                          <div className="hidden md:grid md:grid-cols-10 md:px-4 md:py-3 hover:bg-gray-50 dark:hover:bg-gray-750">
                            <div className="col-span-4">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300">
                                  {user.displayName.charAt(0).toUpperCase()}
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">{user.displayName}</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">{user.username}</div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="col-span-3 flex items-center">
                              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                {user.email}
                              </div>
                            </div>
                            
                            <div className="col-span-2 flex items-center">
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {user.joinDate}
                              </div>
                            </div>
                            
                            <div className="col-span-1 flex items-center">
                              <button 
                                onClick={() => {
                                  setShowReferralModal(false);
                                  handleViewReferrals(user.uid);
                                }}
                                className="text-sm text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 rounded-lg">
                  This user hasn't referred anyone yet
                </p>
              )}
            </div>
          </div>
          
          <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <button
              onClick={() => setShowReferralModal(false)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white rounded-md text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-gray-900 dark:text-white">Admin Dashboard</h1>
      
      {renderNavTabs()}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
        </div>
      )}
      
      {loading ? (
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      ) : (
        <>
          {activeTab === 'users' && renderUsersTab()}
          {activeTab === 'withdrawals' && renderWithdrawalsTab()}
          {activeTab === 'matches' && renderMatchesTab()}
          {activeTab === 'stats' && renderStatsTab()}
          {activeTab === 'support' && renderSupportTab()}
          {activeTab === 'dailycode' && renderDailyCodeTab()}
        </>
      )}
      
      {showReferralModal && selectedUserReferrals && (
        <ReferralModal />
      )}
    </div>
  );
};

export default AdminPanel; 